// Fechas en cristiano.
//
// "2026-07-14T00:00:00Z" no le dice nada a nadie. "Hace 5 semanas" sí,
// y es exactamente la pregunta que se hace alguien parado frente al
// armario: ¿hace cuánto que no me pongo esto?
//
// La precisión al día no importa acá y la exactitud absoluta tampoco:
// entre "hace 37 días" y "hace más de un mes", la segunda se entiende
// sin pensar. Lo único que tiene que quedar clarísimo es el caso
// "nunca", porque es el que dispara la acción.

const DIA = 24 * 60 * 60 * 1000;

/**
 * Cuántos DÍAS DE CALENDARIO pasaron desde una fecha, en hora local.
 * Null si no hay fecha.
 *
 * EL BUG QUE ESTO ARREGLA (2026-08-20)
 *
 * Antes esto era `Math.floor((Date.now() - t) / DIA)`, que cuenta
 * bloques de 24 horas y no días. Una prenda usada ayer a las 18:00 y
 * mirada hoy a las 09:00 son 15 horas: cero bloques, y la app decía
 * "Hoy". Reportado por el dueño exactamente así.
 *
 * El error tiene una asimetría fea: siempre dice que la usaste MÁS
 * RECIENTEMENTE de lo que fue. Y como el orden de las sugerencias es
 * "primero lo que hace más tiempo no usás", eso hunde en la lista
 * prendas que en realidad ya estaban disponibles.
 *
 * Ahora se comparan las fechas del calendario local: ayer es ayer a
 * cualquier hora. Se normalizan las dos a medianoche local, así el
 * horario de verano —que corre el reloj una hora— no puede convertir
 * un día en cero ni en dos.
 */
export function diasDesde(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  const aMedianoche = (f) =>
    new Date(f.getFullYear(), f.getMonth(), f.getDate()).getTime();

  // Redondeo y no piso: entre dos medianoches locales puede haber 23 o
  // 25 horas cuando cambia el horario de verano, y con `floor` un día
  // de 23 horas daría cero.
  return Math.round((aMedianoche(new Date()) - aMedianoche(d)) / DIA);
}

/**
 * "Nunca" / "Hoy" / "Ayer" / "Hace 5 días" / "Hace 3 semanas" / ...
 */
/* ⚠️  EL RESPALDO DE `nunca` ES VACÍO Y NO "Sin usar", Y ES A PROPÓSITO.
 *
 * Decía `nunca = "Sin usar"`. Dos de las cuatro llamadas —Calendario e
 * Historial— NO pasan `nunca`, así que un outfit sin fecha les habría
 * mostrado esas dos palabras EN ESPAÑOL, en coreano o en árabe. Hoy no
 * pasa porque el servidor siempre escribe `usada_en`; o sea que era una
 * fuga latente esperando una fila rara.
 *
 * Este archivo no puede llamar a `t()`: su test lo EVALÚA después de
 * sacarle los `export`, y un `import` ahí adentro no es código válido.
 * Así que el respaldo no puede ser una traducción, y entre vacío y
 * español, vacío. Un hueco se ve y se arregla; español en una pantalla
 * en coreano parece que alguien lo decidió.
 *
 * Quien SÍ tiene algo que decir cuando no hay fecha —Armario, Visor—
 * pasa su propio texto ya traducido. */
export function haceCuanto(iso, { nunca = "", idioma = "es" } = {}) {
  const dias = diasDesde(iso);
  if (dias === null) return nunca;

  /* ⚠️  EL IDIOMA ENTRA POR PARÁMETRO Y NO POR `import`, Y NO ES UN
   *     CAPRICHO: este archivo se EVALÚA en su test después de sacarle
   *     los `export` (ver test_fechas.js). Un `import` ahí adentro no
   *     es código válido y el test entero deja de correr.
   *
   *     Y coincide con lo que este módulo quiere ser: funciones puras
   *     que se prueban con fechas conocidas. Recibir el idioma en vez
   *     de ir a buscarlo lo mantiene puro Y permite probar los doce
   *     idiomas sin montar nada.
   *
   * `numeric: "auto"` es lo que hace que diga "ayer" y no "hace 1 día",
   * "la semana pasada" y no "hace 1 semana". Cada idioma resuelve eso a
   * su manera y ninguna tabla escrita a mano lo habría acertado: el
   * árabe usa el DUAL para dos meses (قبل شهرين, una palabra distinta de
   * "2"), y el japonés no dice "hace 1 día" nunca. */
  const rel = new Intl.RelativeTimeFormat(idioma, { numeric: "auto" });

  const texto = (() => {
    // Una fecha futura es un error de carga, no algo que valga la pena
    // renderizar como "hace -3 días".
    if (dias <= 0) return rel.format(0, "day");
    if (dias === 1) return rel.format(-1, "day");
    if (dias < 7) return rel.format(-dias, "day");

    const semanas = Math.floor(dias / 7);
    if (dias < 60) return rel.format(-semanas, "week");

    const meses = Math.round(dias / 30);
    if (meses < 12) return rel.format(-meses, "month");

    return rel.format(-Math.floor(dias / 365), "year");
  })();

  /* Intl devuelve en minúscula ("hace 5 días") y estos textos empiezan
   * renglón en tres de las cuatro pantallas que los usan. Se sube la
   * primera letra CON el idioma puesto: en árabe, japonés o devanagari
   * no hay mayúsculas y la operación no hace nada, que es lo correcto. */
  return texto.charAt(0).toLocaleUpperCase(idioma) + texto.slice(1);
}

/**
 * Fecha de hoy en formato YYYY-MM-DD, en hora LOCAL.
 *
 * `toISOString()` no sirve para esto: convierte a UTC, así que a la
 * noche en Argentina devuelve la fecha de mañana. Registrar el outfit
 * de hoy con la fecha de mañana rompería la validación de "no futuro"
 * del servidor y, peor, ensuciaría el historial.
 */
export function hoyLocal() {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/**
 * Lo inverso de `aISO`: del ISO guardado al "YYYY-MM-DD" LOCAL que
 * entiende un <input type="date">. Para corregir un registro sin que
 * la fecha se corra un día: `iso.slice(0, 10)` es la fecha en UTC, y un
 * uso registrado a las 22:00 en UTC-3 aparecería al día siguiente.
 */
export function diaLocalDe(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
}

export function hace(dias) {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/**
 * De "2026-08-19" (lo que devuelve un <input type="date">) al ISO que
 * espera el servidor, fijado al mediodía local.
 *
 * El mediodía y no la medianoche: medianoche local en UTC-3 es las 03:00
 * UTC del día siguiente, y un outfit registrado hoy a la noche caería en
 * el día equivocado. Al mediodía no hay zona horaria que lo mueva de día.
 */
export function aISO(fecha) {
  if (!fecha) return null;
  const [a, m, d] = fecha.split("-").map(Number);
  if (!a || !m || !d) return null;
  return new Date(a, m - 1, d, 12, 0, 0).toISOString();
}
